import { useEffect, useState } from 'react'
import { CategoryCard } from '../components/MenuComponents'
import '../components/QrMenu.css'
import apiConfig from '../config/api'

const colors = {
  primary: '#8A1538',
  secondary: '#A98B60',
  accent: '#D7BE83',
  light: '#FFFFFF',
  dark: '#333333',
  background: '#F9F7F2',
}

const QrPage = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [openCategories, setOpenCategories] = useState({})
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 })
  const [categoryPositions, setCategoryPositions] = useState({})
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Función para obtener datos (usada en carga inicial y polling)
  const fetchData = async (isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) {
        setLoading(true)
      } else {
        setIsRefreshing(true)
      }
      setError(null)

      const firstPageUrl = `${apiConfig.baseURL}${apiConfig.endpoints.products}?page=1`

      // Cargar primera página de productos
      const prodRes = await fetch(firstPageUrl).catch(e => {
        throw new Error('Error fetching products: ' + e.message)
      })

      // Procesar Productos
      const firstData = await prodRes.json()

      // Procesar Categorías desde la respuesta de productos (no requiere llamada separada)
      if (firstData?.data?.categories) {
         try {
           const cats = firstData.data.categories
           if (Array.isArray(cats)) {
             const positions = {}
             // Filtrar solo categorías activas (si active no existe, asumimos que está activa)
             cats.filter(cat => cat.active !== false).forEach(cat => {
               positions[cat.id] = typeof cat.position === 'number' ? cat.position : 999
             })
             setCategoryPositions(positions)
           }
         } catch (e) {
           console.error('Error parsing categories:', e)
         }
      }
      if (!firstData?.data?.products) {
        setError('Formato de respuesta de API de productos inválido')
        setProducts([])
        return
      }

      let allProducts = [...firstData.data.products]
      const totalPages = firstData?.data?.pagination?.total_pages || 1

      if (!isBackgroundRefresh) {
        setLoadingProgress({ current: 1, total: totalPages })
      }

      // Cargar resto de páginas
      for (let page = 2; page <= totalPages; page++) {
        try {
          const pageUrl = `${apiConfig.baseURL}${apiConfig.endpoints.products}?page=${page}`
          const res = await fetch(pageUrl)
          const data = await res.json()
          if (data?.data?.products) {
            allProducts = allProducts.concat(data.data.products)
            if (!isBackgroundRefresh) {
              setLoadingProgress({ current: page, total: totalPages })
            }
          }
        } catch {
           // ignorar error de página individual
        }
      }

      const onlyActive = allProducts.filter(p => p?.available === true)
      const normalized = onlyActive.map(p => ({
        ...p,
        category:
          p.category && typeof p.category === 'object'
            ? { id: p.category.id, name: p.category.name || 'Sin categoría' }
            : p.category,
      }))

      setProducts(normalized)

    } catch (err) {
      if (!isBackgroundRefresh) {
        setError(`Error al cargar datos: ${err?.message || 'Error desconocido'}`)
      } else {
        console.error('Error en actualización en background:', err)
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false)
      } else {
        setIsRefreshing(false)
      }
    }
  }

  useEffect(() => {
    // Carga inicial
    fetchData(false)

    // Configurar polling cada 30 segundos
    const pollingInterval = setInterval(() => {
      fetchData(true)
    }, 30000) // 30 segundos

    // Cleanup: limpiar el intervalo cuando el componente se desmonte
    return () => {
      clearInterval(pollingInterval)
    }
  }, [])

  const categories = products.reduce((acc, product) => {
    const catId = product?.category_id
    if (catId === undefined || catId === null) return acc

    // Solo incluir productos de categorías activas (que están en categoryPositions)
    if (categoryPositions[catId] === undefined) return acc

    if (!acc[catId]) {
      acc[catId] = {
        id: catId,
        name: product?.category?.name || 'Sin categoría',
        // Usar la posición del mapa de posiciones si existe, sino 999
        position: categoryPositions[catId] ?? 999,
        products: [],
      }
    }
    acc[catId].products.push(product)
    return acc
  }, {})

  const categoriesArr = Object.values(categories)
    .map(cat => ({
      ...cat,
      products: cat.products
        .filter(p => p?.available === true)
        .filter(p => (p?.name || '').toLowerCase().includes(search.toLowerCase()))
        // Ordenar productos alfabéticamente dentro de cada categoría
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '', 'es')),
    }))
    .filter(cat => cat.products.length > 0)
    // Ordenar categorías según el campo position del backend
    .sort((a, b) => {
      // Primero ordenar por position (las que tienen position van primero)
      const posA = a.position ?? 999
      const posB = b.position ?? 999
      if (posA !== posB) return posA - posB
      // Si tienen la misma position, ordenar alfabéticamente
      return a.name.localeCompare(b.name, 'es')
    })

  const reloadData = () => {
    setLoading(true)
    setError(null)
    setProducts([])
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  return (
    <div style={{ backgroundColor: colors.background }} className='min-h-screen w-full overflow-x-hidden'>
      <div style={{ backgroundColor: colors.primary }} className='sticky top-0 z-10 shadow-md w-full'>
        <div className='container mx-auto px-4 py-6 max-w-full'>
          {/* Indicador de actualización en background */}
          {isRefreshing && (
            <div className='absolute top-2 right-2 flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full'>
              <div className='w-2 h-2 bg-white rounded-full animate-pulse'></div>
              <span className='text-xs text-white'>Actualizando...</span>
            </div>
          )}

          <h1 className='text-3xl font-bold text-white text-center mb-2 break-words'>
            <span className='font-light'>arveta</span>
            <span className='mx-2'>·</span>
            <span className='font-normal'>house</span>
          </h1>
          <p className='text-center text-sm italic mb-4' style={{ color: colors.accent }}>
            coffee & brunch
          </p>

          <div className='relative mx-auto max-w-md w-full px-2'>
            <input
              type='text'
              placeholder='Buscar productos...'
              className='w-full pl-10 pr-4 py-2 rounded-full border-none'
              style={{ boxShadow: `0 0 0 2px ${colors.accent}`, outline: 'none', color: colors.dark }}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className='absolute left-5 top-2.5'>
              <svg
                className='h-5 w-5'
                style={{ color: colors.secondary }}
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className='w-full px-2 sm:px-4 py-8 overflow-x-hidden'>
        <div className='max-w-4xl mx-auto'>
          {loading && (
            <div
              style={{ backgroundColor: colors.light }}
              className='rounded-lg p-4 sm:p-6 shadow-md text-center my-4 mx-2 break-words'
            >
              <p style={{ color: colors.secondary }} className='mb-2'>
                Cargando productos...
              </p>
              {loadingProgress.total > 0 && (
                <div className='w-full max-w-md mx-auto px-2'>
                  <div className='relative pt-1'>
                    <div className='flex items-center justify-between'>
                      <span style={{ color: colors.primary }} className='text-xs font-semibold inline-block'>
                        {Math.round((loadingProgress.current / loadingProgress.total) * 100)}%
                      </span>
                      <span style={{ color: colors.secondary }} className='text-xs font-semibold inline-block'>
                        Página {loadingProgress.current} de {loadingProgress.total}
                      </span>
                    </div>
                    <div className='overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200 mt-1'>
                      <div
                        style={{
                          width: `${(loadingProgress.current / loadingProgress.total) * 100}%`,
                          backgroundColor: colors.primary,
                        }}
                        className='shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center'
                      />
                    </div>
                  </div>
                </div>
              )}
              <p className='text-xs italic mt-3 px-2' style={{ color: colors.secondary }}>
                Estamos cargando todas las categorías del menú, por favor espere...
              </p>
            </div>
          )}

          {error && (
            <div
              style={{ backgroundColor: colors.light }}
              className='rounded-lg p-4 sm:p-6 shadow-md text-center my-4 mx-2'
            >
              <p style={{ color: colors.primary }} className='mb-3 break-words'>
                {error}
              </p>
              <button
                onClick={reloadData}
                className='px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors'
              >
                Intentar nuevamente
              </button>
            </div>
          )}

          {!loading && !error && categoriesArr.length === 0 && (
            <div
              style={{ backgroundColor: colors.light }}
              className='rounded-lg p-4 sm:p-6 shadow-md text-center my-4 mx-2'
            >
              <p style={{ color: colors.secondary }}>No hay productos activos que coincidan con la búsqueda</p>
            </div>
          )}

          {!loading && !error && categoriesArr.length > 0 && (
            <div className='space-y-4 mx-2'>
              {categoriesArr.map(cat => (
                <CategoryCard
                  key={cat.id}
                  category={cat}
                  colors={colors}
                  isOpen={!!openCategories[cat.id]}
                  onToggle={() =>
                    setOpenCategories(prev => ({
                      ...prev,
                      [cat.id]: !prev[cat.id],
                    }))
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default QrPage
